import * as constants from '../../../utils/constants';
import * as domainLogics from '../../logics';
import * as domainUtils from '../../utils';
import * as textLogics from '../../../text/logics';
import * as textSelectors from '../../../text/selectors';

import { getRectPoints, rotateText } from '../../../canvas/logics';

/** keep the element inside the canvas
 * NOTE: you need to check if the element can fit the canvas before calling this function
 * @param  {Object} draggingArea
 * @param  {Object} elementPos - {x, y}
 * @param  {Object} elementSize - {width, height}
 * @param  {number} direction
 * @returns adjusted element: {x, y, width, height, direction}
 */
export const keepElementInCanvas = function (draggingArea, elementPos, elementSize, direction) {
  const displayElement = domainUtils.convertRealElementToDisplayElement({
    x: elementPos.x,
    y: elementPos.y,
    width: elementSize.width,
    height: elementSize.height,
    direction,
  });

  if (displayElement.x < draggingArea.x) {
    displayElement.x = draggingArea.x;
  }
  if (displayElement.y < draggingArea.y) {
    displayElement.y = draggingArea.y;
  }
  if (displayElement.x + displayElement.width > draggingArea.x + draggingArea.width) {
    displayElement.x = draggingArea.width - displayElement.width + draggingArea.x;
  }
  if (displayElement.y + displayElement.height > draggingArea.y + draggingArea.height) {
    displayElement.y = draggingArea.height - displayElement.height + draggingArea.y;
  }

  const realElement = domainUtils.convertDisplayElementToRealElement({
    x: displayElement.x,
    y: displayElement.y,
    width: displayElement.width,
    height: displayElement.height,
    direction,
  });
  return realElement;
};

export const keepTextElementInCanvas = function (draggingArea, element, elementSize, centerText) {
  const displayElement = { x: element.ui.outlineRect.x, y: element.ui.outlineRect.y, ...elementSize };
  let newX = displayElement.x;
  let newY = displayElement.y;
  let diffX = 0;
  let diffY = 0;

  if (displayElement.x < draggingArea.x) {
    newX = draggingArea.x;
  }
  if (displayElement.y < draggingArea.y) {
    newY = draggingArea.y;
  }
  if (displayElement.x + displayElement.width > draggingArea.x + draggingArea.width) {
    newX = draggingArea.width - displayElement.width + draggingArea.x;
  }
  if (centerText) newX = (draggingArea.width - displayElement.width) / 2 + draggingArea.x;

  if (displayElement.y + displayElement.height > draggingArea.y + draggingArea.height) {
    newY = draggingArea.height - displayElement.height + draggingArea.y;
  }
  if (centerText) newY = (draggingArea.height - displayElement.height) / 2 + draggingArea.y;

  diffX = newX - displayElement.x;
  diffY = newY - displayElement.y;

  const realNoRatioElement = {
    x: element.data.x + diffX,
    y: element.data.y + diffY,
    width: displayElement.width,
    height: displayElement.height,
  };
  return realNoRatioElement;
};

export const getResultAfterKeepElementInSide = function (
  draggingArea,
  currentPos,
  noRotateRect,
  direction,
  elementType = constants.elementType.Text
) {
  let currentNoRotateRect = { ...noRotateRect, x: currentPos.x, y: currentPos.y };

  const rotateResult = rotateText(getRectPoints(currentNoRotateRect), direction);
  let outlineRect = rotateResult.outlineRect;
  let x = 0;
  if (draggingArea.isCalendar) {
    // if (elementType === constants.elementType.Image && outlineRect.width > draggingArea.sideWidth) {
    //   // image element and width is greater than the side width --> no need to adjust
    //   return x;
    // }

    const isOnFoldLine = domainUtils.isOnFoldLine(draggingArea, elementType, outlineRect.x, outlineRect.width);
    if (isOnFoldLine) {
      if (outlineRect.x + outlineRect.width / 2 - draggingArea.x < draggingArea.designCenter) {
        // most part is on the left
        x = draggingArea.foldLeft - outlineRect.width + draggingArea.x;
      } else {
        // most part is on the right
        x = draggingArea.foldRight + draggingArea.x;
      }

      const offset = x - outlineRect.x;
      rotateResult.rotatedRectPoints[0].x += offset;
      rotateResult.rotatedRectPoints[1].x += offset;
      rotateResult.rotatedRectPoints[2].x += offset;
      rotateResult.rotatedRectPoints[3].x += offset;
      return {
        x: x + outlineRect.width / 2 - noRotateRect.width / 2,
        outlineRect,
        rotatedRectPoints: rotateResult.rotatedRectPoints,
      };
    }
  }
  return { x: currentPos.x, outlineRect, rotatedRectPoints: rotateResult.rotatedRectPoints };
};

export const getRealPosToKeepElementInside = function (
  draggingArea,
  currentPos,
  noRotateRect,
  direction,
  elementType = constants.elementType.Text
) {
  let currentNoRotateRect = { ...noRotateRect, x: currentPos.x, y: currentPos.y };

  const rotateResult = rotateText(getRectPoints(currentNoRotateRect), direction);
  let outlineRect = rotateResult.outlineRect;
  let x = currentPos.x;
  let y = currentPos.y;

  if (draggingArea.isCalendar) {
    const isOnFoldLine = domainUtils.isOnFoldLine(draggingArea, elementType, outlineRect.x, outlineRect.width);
    if (isOnFoldLine) {
      if (outlineRect.x + outlineRect.width / 2 - draggingArea.x < draggingArea.designCenter) {
        // most part is on the left
        x = draggingArea.foldLeft - outlineRect.width + draggingArea.x;
      } else {
        // most part is on the right
        x = draggingArea.foldRight + draggingArea.x;
      }
      x = x + outlineRect.width / 2 - noRotateRect.width / 2;
    }
  } else if (outlineRect.x < draggingArea.x) {
    x = currentPos.x + draggingArea.x - outlineRect.x;
  }

  if (outlineRect.y < draggingArea.y) {
    y = currentPos.y + draggingArea.y - outlineRect.y;
  }

  return { x, y };
};


export const getRealXAfterKeepElementInSide = function (
  draggingArea,
  currentPos,
  noRotateRect,
  direction,
  elementType = constants.elementType.Text
) {
  let currentNoRotateRect = { ...noRotateRect, x: currentPos.x, y: currentPos.y };

  const rotateResult = rotateText(getRectPoints(currentNoRotateRect), direction);
  let outlineRect = rotateResult.outlineRect;
  let x = 0;
  if (draggingArea.isCalendar) {
    // if (elementType === constants.elementType.Image && outlineRect.width > draggingArea.sideWidth) {
    //   // image element and width is greater than the side width --> no need to adjust
    //   return x;
    // }

    const isOnFoldLine = domainUtils.isOnFoldLine(draggingArea, elementType, outlineRect.x, outlineRect.width);
    if (isOnFoldLine) {
      if (outlineRect.x + outlineRect.width / 2 - draggingArea.x < draggingArea.designCenter) {
        // most part is on the left
        x = draggingArea.foldLeft - outlineRect.width + draggingArea.x;
      } else {
        // most part is on the right
        x = draggingArea.foldRight + draggingArea.x;
      }
      return x + outlineRect.width / 2 - noRotateRect.width / 2;
    }
  }

  if (outlineRect.x < draggingArea.x) {
    return currentPos.x + draggingArea.x - outlineRect.x;
  }

  return currentPos.x;
};

export const getRealYAfterKeepElementInSide = function (draggingArea, currentPos, noRotateRect, direction) {
  let currentNoRotateRect = { ...noRotateRect, x: currentPos.x, y: currentPos.y };

  const rotateResult = rotateText(getRectPoints(currentNoRotateRect), direction);

  let outlineRect = rotateResult.outlineRect;

  if (outlineRect.y < draggingArea.y) {
    return currentPos.y + draggingArea.y - outlineRect.y;
  }

  return currentPos.y;
};

/** keep the element in the correct side of the calendar
 * NOTE: you need to make sure the whole element is inside the canvas and check if the element can fit the side before calling this function
 * @param  {Object} draggingArea
 * @param  {Object} elementPos - real element pos
 * @param  {Object} elementSize - real element size
 * @param  {number} direction
 * @param  {enum} elementType - constants.elementType
 * @returns adjusted real element: {x, y, width, height, direction}
 */
export const keepElementInSide = function (
  draggingArea,
  elementPos,
  elementSize,
  direction,
  elementType = constants.elementType.Text,
  isNextgenUi
) {
  let realElement = {
    x: elementPos.x,
    y: elementPos.y,
    width: elementSize.width,
    height: elementSize.height,
    direction,
  };
  if (draggingArea.isCalendar) {
    const displayElement = isNextgenUi ? realElement : domainUtils.convertRealElementToDisplayElement(realElement);
    if (elementType === constants.elementType.Image && displayElement.width > draggingArea.sideWidth) {
      // image element and width is greater than the side width --> no need to adjust
      return realElement;
    }

    const isOnFoldLine = domainUtils.isOnFoldLine(draggingArea, elementType, displayElement.x, displayElement.width);
    if (isOnFoldLine) {
      if (displayElement.x - draggingArea.x + displayElement.width / 2 < draggingArea.designCenter) {
        // most part is on the left
        displayElement.x = draggingArea.foldLeft - displayElement.width + draggingArea.x;
      } else {
        // most part is on the right
        displayElement.x = draggingArea.foldRight + draggingArea.x;
      }
      realElement = isNextgenUi ? displayElement : domainUtils.convertDisplayElementToRealElement(displayElement);
    }
  }
  return realElement;
};

export const keepTextElementInSide = function (
  draggingArea,
  elementPos,
  elementSize,
  direction,
  elementType = constants.elementType.Text
) {
  const rotateResult = rotateText(
    [
      { x: elementPos.x, y: elementPos.y },
      { x: elementPos.x + elementSize.width, y: elementPos.y },
      { x: elementPos.x + elementSize.width, y: elementPos.y + elementSize.height },
      { x: elementPos.x, y: elementPos.y + elementSize.height },
    ],
    direction
  );
  let outlineRect = rotateResult.outlineRect;
  let realElement = {
    x: elementPos.x,
    y: elementPos.y,
    width: elementSize.width,
    height: elementSize.height,
    direction,
  };
  if (draggingArea.isCalendar) {
    if (elementType === constants.elementType.Image && outlineRect.width > draggingArea.sideWidth) {
      // image element and width is greater than the side width --> no need to adjust
      return outlineRect;
    }
    let realX = 0;
    const isOnFoldLine = domainUtils.isOnFoldLine(draggingArea, elementType, outlineRect.x, outlineRect.width);
    if (isOnFoldLine) {
      if (outlineRect.x - draggingArea.x + outlineRect.width / 2 < draggingArea.designCenter) {
        // most part is on the left
        realX = draggingArea.foldLeft - outlineRect.width + draggingArea.x;
      } else {
        // most part is on the right
        realX = draggingArea.foldRight + draggingArea.x;
      }
      realElement.x = realElement.x + (realX - outlineRect.x);
    }
  }
  return realElement;
};

/** align element position in canvas
 * @param  {Object} draggingArea
 * @param  {Object} elementPos - real position {x, y}
 * @param  {Object} elementSize - real element size {width, height}
 * @param  {number} direction
 * @returns {number} aligned x
 */
export const horizonAlignElement = function (draggingArea, elementPos, elementSize, direction, horizonAlign, isText) {
  let displayElement = {};
  if (isText) {
    displayElement = { x: elementPos.x, y: elementPos.y, width: elementSize.width, height: elementSize.height };
  } else {
    displayElement = domainUtils.convertRealElementToDisplayElement({ ...elementPos, ...elementSize, direction });
  }

  if (draggingArea.isCalendar && (displayElement.width <= draggingArea.sideWidth || isText)) {
    const sideType =
      displayElement.x < draggingArea.foldLeft
        ? constants.calendarSideType.LeftSide
        : constants.calendarSideType.RightSide;
    const alignX = domainUtils.horizaonAlignSide(
      draggingArea,
      { x: displayElement.x, y: displayElement.y },
      { width: displayElement.width, height: displayElement.height },
      horizonAlign,
      sideType
    );
    displayElement.x = alignX;
  } else {
    //it should be image element and its width is greater than the side width
    const alignX = domainUtils.horizonAlignCanvas(
      draggingArea,
      { x: displayElement.x, y: displayElement.y },
      { width: displayElement.width, height: displayElement.height },
      horizonAlign
    );
    displayElement.x = alignX;
  }
  const realElement = domainUtils.convertDisplayElementToRealElement(displayElement);
  return isText ? displayElement.x : realElement.x;
};

export const verticalAlignElement = function (draggingArea, elementPos, elementSize, direction, verticalAlign, isText) {
  let displayElement = {};
  if (isText) {
    displayElement = { x: elementPos.x, y: elementPos.y, width: elementSize.width, height: elementSize.height };
  } else {
    displayElement = domainUtils.convertRealElementToDisplayElement({ ...elementPos, ...elementSize, direction });
  }

  const alignY = domainUtils.verticalAlignCanvas(
    draggingArea,
    { x: displayElement.x, y: displayElement.y },
    { width: displayElement.width, height: displayElement.height },
    verticalAlign
  );
  displayElement.y = alignY;
  const realElement = domainUtils.convertDisplayElementToRealElement(displayElement);
  return isText ? displayElement.y : realElement.y;
};

/** get adjusting offset in order to keep the element inside the canvas during dragging
 * @param  {Object} draggingArea
 * @param  {array} clientRect
 * @returns {Object} offset: {offsetX, offsetY}
 */
export const getAdjustOffset = function (draggingArea, clientRect) {
  const offset = { offsetX: 0, offsetY: 0 };
  const outLeftBorder = clientRect.some((pos) => pos.x < draggingArea.x);
  const outRightBorder = clientRect.some((pos) => pos.x > draggingArea.x + draggingArea.width);
  const outTopBorder = clientRect.some((pos) => pos.y < draggingArea.y);
  const outBottomBorder = clientRect.some((pos) => pos.y > draggingArea.y + draggingArea.height);
  if (outLeftBorder) {
    clientRect.forEach((pos) => {
      offset.offsetX = Math.max(offset.offsetX, draggingArea.x - pos.x);
    });
  } else if (outRightBorder) {
    clientRect.forEach((pos) => {
      offset.offsetX = Math.min(offset.offsetX, draggingArea.x + draggingArea.width - pos.x);
    });
  }
  if (outTopBorder) {
    clientRect.forEach((pos) => {
      offset.offsetY = Math.max(offset.offsetY, draggingArea.y - pos.y);
    });
  } else if (outBottomBorder) {
    clientRect.forEach((pos) => {
      offset.offsetY = Math.min(offset.offsetY, draggingArea.y + draggingArea.height - pos.y);
    });
  }

  return offset;
};

/** adjust element position by the dragging offset
 * @param  {Object} offset - dragging offset
 * @param  {array} clientRect
 * @returns {array} clientRect
 */
export const adjustClientRect = function (offset, clientRect) {
  clientRect.forEach((pos) => {
    pos.x = pos.x + offset.offsetX;
    pos.y = pos.y + offset.offsetY;
  });
  return clientRect;
};

/** check if the element size is between max and min size allowed
 * @param  {Object} elementSize - {width, height}
 * @param  {Object} elementSizeRange - {minWidth, minHeight, maxWidth, maxHeight}
 * @returns {boolean} check result
 */
export const checkElementSize = function (elementSize, elementSizeRange) {
  if (elementSize.width < elementSizeRange.minWidth || elementSize.height < elementSizeRange.minHeight) {
    return false;
  } else if (elementSize.width > elementSizeRange.maxWidth || elementSize.height > elementSizeRange.maxHeight) {
    return false;
  }
  return true;
};

export const processResizeAbsolutePos = function (absoluteGroupPos, resizeAbolutePos, direction, elementSizeRange) {
  const newElementSize = {};
  const newResizeAbsolutePos = {};
  if (direction === 90) {
    if (elementSizeRange.maxWidth < elementSizeRange.maxHeight) {
      //use x to calc
      if (absoluteGroupPos.x - resizeAbolutePos.x > elementSizeRange.maxHeight) {
        //greater than max
        newElementSize.width = elementSizeRange.maxWidth;
        newElementSize.height = elementSizeRange.maxHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x - elementSizeRange.maxHeight;
        newResizeAbsolutePos.y = absoluteGroupPos.y + elementSizeRange.maxWidth;
      } else if (absoluteGroupPos.x - resizeAbolutePos.x < elementSizeRange.minHeight) {
        //smaller than min
        newElementSize.width = elementSizeRange.minWidth;
        newElementSize.height = elementSizeRange.minHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x - elementSizeRange.minHeight;
        newResizeAbsolutePos.y = absoluteGroupPos.y - elementSizeRange.minWidth;
      } else {
        newElementSize.height = absoluteGroupPos.x - resizeAbolutePos.x;
        newElementSize.width = domainLogics.getWidthByHeight(
          newElementSize.height,
          elementSizeRange.maxWidth,
          elementSizeRange.maxHeight
        );
        newResizeAbsolutePos.x = resizeAbolutePos.x;
        newResizeAbsolutePos.y = absoluteGroupPos.y + newElementSize.width;
      }
    } else {
      //use y to calc
      if (resizeAbolutePos.y - absoluteGroupPos.y > elementSizeRange.maxWidth) {
        newElementSize.width = elementSizeRange.maxWidth;
        newElementSize.height = elementSizeRange.maxHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x - elementSizeRange.maxHeight;
        newResizeAbsolutePos.y = absoluteGroupPos.y + elementSizeRange.maxWidth;
      } else if (resizeAbolutePos.y - absoluteGroupPos.y < elementSizeRange.minWidth) {
        newElementSize.width = elementSizeRange.minWidth;
        newElementSize.height = elementSizeRange.minHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x - elementSizeRange.minHeight;
        newResizeAbsolutePos.y = absoluteGroupPos.y + elementSizeRange.minWidth;
      } else {
        newElementSize.width = resizeAbolutePos.y - absoluteGroupPos.y;
        newElementSize.height = domainLogics.getHeightByWidth(
          newElementSize.width,
          elementSizeRange.maxWidth,
          elementSizeRange.maxHeight
        );
        newResizeAbsolutePos.y = resizeAbolutePos.y;
        newResizeAbsolutePos.x = absoluteGroupPos.x - newElementSize.height;
      }
    }
  } else if (direction === 180) {
    if (elementSizeRange.maxWidth > elementSizeRange.maxHeight) {
      //use x to calc
      if (absoluteGroupPos.x - resizeAbolutePos.x > elementSizeRange.maxWidth) {
        //greater than max
        newElementSize.width = elementSizeRange.maxWidth;
        newElementSize.height = elementSizeRange.maxHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x - elementSizeRange.maxWidth;
        newResizeAbsolutePos.y = absoluteGroupPos.y - elementSizeRange.maxHeight;
      } else if (absoluteGroupPos.x - resizeAbolutePos.x < elementSizeRange.minWidth) {
        //smaller than min
        newElementSize.width = elementSizeRange.minWidth;
        newElementSize.height = elementSizeRange.minHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x - elementSizeRange.minWidth;
        newResizeAbsolutePos.y = absoluteGroupPos.y - elementSizeRange.minHeight;
      } else {
        newElementSize.width = absoluteGroupPos.x - resizeAbolutePos.x;
        newElementSize.height = domainLogics.getHeightByWidth(
          newElementSize.width,
          elementSizeRange.maxWidth,
          elementSizeRange.maxHeight
        );
        newResizeAbsolutePos.x = resizeAbolutePos.x;
        newResizeAbsolutePos.y = absoluteGroupPos.y - newElementSize.height;
      }
    } else {
      //use y to calc
      if (absoluteGroupPos.y - resizeAbolutePos.y > elementSizeRange.maxHeight) {
        newElementSize.width = elementSizeRange.maxWidth;
        newElementSize.height = elementSizeRange.maxHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x - elementSizeRange.maxWidth;
        newResizeAbsolutePos.y = absoluteGroupPos.y - elementSizeRange.maxHeight;
      } else if (absoluteGroupPos.y - resizeAbolutePos.y < elementSizeRange.minHeight) {
        newElementSize.width = elementSizeRange.minWidth;
        newElementSize.height = elementSizeRange.minHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x - elementSizeRange.minWidth;
        newResizeAbsolutePos.y = absoluteGroupPos.y - elementSizeRange.minHeight;
      } else {
        newElementSize.height = absoluteGroupPos.y - resizeAbolutePos.y;
        newElementSize.width = domainLogics.getWidthByHeight(
          newElementSize.height,
          elementSizeRange.maxWidth,
          elementSizeRange.maxHeight
        );
        newResizeAbsolutePos.y = resizeAbolutePos.y;
        newResizeAbsolutePos.x = absoluteGroupPos.x - newElementSize.width;
      }
    }
  } else if (direction === 270) {
    if (elementSizeRange.maxWidth < elementSizeRange.maxHeight) {
      //use x to calc
      if (resizeAbolutePos.x - absoluteGroupPos.x > elementSizeRange.maxHeight) {
        //greater than max
        newElementSize.width = elementSizeRange.maxWidth;
        newElementSize.height = elementSizeRange.maxHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x + elementSizeRange.maxHeight;
        newResizeAbsolutePos.y = absoluteGroupPos.y - elementSizeRange.maxWidth;
      } else if (resizeAbolutePos.x - absoluteGroupPos.x < elementSizeRange.minHeight) {
        //smaller than min
        newElementSize.width = elementSizeRange.minWidth;
        newElementSize.height = elementSizeRange.minHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x + elementSizeRange.minHeight;
        newResizeAbsolutePos.y = absoluteGroupPos.y - elementSizeRange.minWidth;
      } else {
        newElementSize.height = resizeAbolutePos.x - absoluteGroupPos.x;
        newElementSize.width = domainLogics.getWidthByHeight(
          newElementSize.height,
          elementSizeRange.maxWidth,
          elementSizeRange.maxHeight
        );
        newResizeAbsolutePos.x = resizeAbolutePos.x;
        newResizeAbsolutePos.y = absoluteGroupPos.y - newElementSize.width;
      }
    } else {
      //use y to calc
      if (absoluteGroupPos.y - resizeAbolutePos.y > elementSizeRange.maxWidth) {
        newElementSize.width = elementSizeRange.maxWidth;
        newElementSize.height = elementSizeRange.maxHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x + elementSizeRange.maxHeight;
        newResizeAbsolutePos.y = absoluteGroupPos.y - elementSizeRange.maxWidth;
      } else if (absoluteGroupPos.y - resizeAbolutePos.y < elementSizeRange.minWidth) {
        newElementSize.width = elementSizeRange.minWidth;
        newElementSize.height = elementSizeRange.minHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x + elementSizeRange.minHeight;
        newResizeAbsolutePos.y = absoluteGroupPos.y - elementSizeRange.minWidth;
      } else {
        newElementSize.width = absoluteGroupPos.y - resizeAbolutePos.y;
        newElementSize.height = domainLogics.getHeightByWidth(
          newElementSize.width,
          elementSizeRange.maxWidth,
          elementSizeRange.maxHeight
        );
        newResizeAbsolutePos.y = resizeAbolutePos.y;
        newResizeAbsolutePos.x = absoluteGroupPos.x + newElementSize.height;
      }
    }
  } else {
    if (elementSizeRange.maxWidth > elementSizeRange.maxHeight) {
      //use x to calc
      if (resizeAbolutePos.x - absoluteGroupPos.x > elementSizeRange.maxWidth) {
        //greater than max
        newElementSize.width = elementSizeRange.maxWidth;
        newElementSize.height = elementSizeRange.maxHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x + elementSizeRange.maxWidth;
        newResizeAbsolutePos.y = absoluteGroupPos.y + elementSizeRange.maxHeight;
      } else if (resizeAbolutePos.x - absoluteGroupPos.x < elementSizeRange.minHeight) {
        //smaller than min
        newElementSize.width = elementSizeRange.minWidth;
        newElementSize.height = elementSizeRange.minHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x + elementSizeRange.minWidth;
        newResizeAbsolutePos.y = absoluteGroupPos.y + elementSizeRange.minHeight;
      } else {
        newElementSize.width = resizeAbolutePos.x - absoluteGroupPos.x;
        newElementSize.height = domainLogics.getHeightByWidth(
          newElementSize.width,
          elementSizeRange.maxWidth,
          elementSizeRange.maxHeight
        );
        newResizeAbsolutePos.x = resizeAbolutePos.x;
        newResizeAbsolutePos.y = absoluteGroupPos.y + newElementSize.height;
      }
    } else {
      //use y to calc
      if (resizeAbolutePos.y - absoluteGroupPos.y > elementSizeRange.maxHeight) {
        newElementSize.width = elementSizeRange.maxWidth;
        newElementSize.height = elementSizeRange.maxHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x + elementSizeRange.maxWidth;
        newResizeAbsolutePos.y = absoluteGroupPos.y + elementSizeRange.maxHeight;
      } else if (resizeAbolutePos.y - absoluteGroupPos.y < elementSizeRange.minHeight) {
        newElementSize.width = elementSizeRange.minWidth;
        newElementSize.height = elementSizeRange.minHeight;
        newResizeAbsolutePos.x = absoluteGroupPos.x + elementSizeRange.minWidth;
        newResizeAbsolutePos.y = absoluteGroupPos.y + elementSizeRange.minHeight;
      } else {
        newElementSize.height = resizeAbolutePos.y - absoluteGroupPos.y;
        newElementSize.width = domainLogics.getWidthByHeight(
          newElementSize.height,
          elementSizeRange.maxWidth,
          elementSizeRange.maxHeight
        );
        newResizeAbsolutePos.y = resizeAbolutePos.y;
        newResizeAbsolutePos.x = absoluteGroupPos.x + newElementSize.width;
      }
    }
  }

  return { newElementSize, newResizeAbsolutePos };
  //new element size
  //new resize icon pos
};

export const getCurrentResizeAbsolutePos = function (groupAbsolutePosition, elementSize, direction) {
  const pos = {};
  if (direction === 90) {
    pos.x = groupAbsolutePosition.x - elementSize.height;
    pos.y = groupAbsolutePosition.y + elementSize.width;
  } else if (direction === 180) {
    pos.x = groupAbsolutePosition.x - elementSize.width;
    pos.y = groupAbsolutePosition.y - elementSize.height;
  } else if (direction === 270) {
    pos.x = groupAbsolutePosition.x + elementSize.height;
    pos.y = groupAbsolutePosition.y - elementSize.width;
  } else {
    pos.x = groupAbsolutePosition.x + elementSize.width;
    pos.y = groupAbsolutePosition.y + elementSize.height;
  }
  return pos;
};

export const correctElementSize = function (elementSize, maxWidth, maxHeight, callback) {
  return (_, getState) => {
    const state = getState();
    const fontSizeRange = textSelectors.getFontSizeRange(state);
    const result = textLogics.getFontSizeByElementSize(elementSize, fontSizeRange, maxWidth, maxHeight);
    callback(result);
  };
};

export const getActionButtonOffsets = function (
  elementPosition,
  elementSize,
  draggingArea,
  direction,
  isMobileOnly,
  canvasOverVignetteConfig,
  scale
) {
  const actionButtonSize = domainUtils.ActionButtonSize;

  const elementLeftTop = { x: elementPosition.x * scale, y: elementPosition.y * scale };
  const elementRightBottom = {
    x: (elementPosition.x + elementSize.width) * scale,
    y: (elementPosition.y + elementSize.height) * scale,
  };
  const elementRightTop = { x: (elementPosition.x + elementSize.width) * scale, y: elementPosition.y * scale };

  const elementRightTop90 = { x: elementPosition.x * scale, y: elementPosition.y * scale };
  const elementLeftBottom90 = {
    x: (elementPosition.x - elementSize.height) * scale,
    y: (elementPosition.y + elementSize.width) * scale,
  };
  const elementRightBottom90 = { x: elementPosition.x * scale, y: (elementPosition.y + elementSize.width) * scale };

  const elementLeftTop180 = {
    x: (elementPosition.x - elementSize.width) * scale,
    y: (elementPosition.y - elementSize.height) * scale,
  };
  const elementRightBottom180 = { x: elementPosition.x * scale, y: elementPosition.y * scale };
  const elementLeftBottom180 = { x: (elementPosition.x - elementSize.width) * scale, y: elementPosition.y * scale };

  const elementRightTop270 = {
    x: (elementPosition.x + elementSize.height) * scale,
    y: (elementPosition.y - elementSize.width) * scale,
  };
  const elementLeftBottom270 = { x: elementPosition.x * scale, y: elementPosition.y * scale };
  const elementLeftTop270 = { x: elementPosition.x * scale, y: (elementPosition.y - elementSize.width) * scale };

  const draggingAreaRightTop = { x: (draggingArea.x + draggingArea.width) * scale, y: draggingArea.y * scale };
  const draggingAreaRightBottom = {
    x: (draggingArea.x + draggingArea.width) * scale,
    y: (draggingArea.y + draggingArea.height) * scale,
  };

  if (!isMobileOnly && !canvasOverVignetteConfig) {
    return {
      deleteOffset: { x: actionButtonSize, y: actionButtonSize },
      rotateOffset: { x: actionButtonSize - actionButtonSize, y: actionButtonSize },
      resizeOffset: { x: actionButtonSize - actionButtonSize, y: actionButtonSize - actionButtonSize },
    };
  }

  if (direction === 90) {
    return {
      deleteOffset: {
        x: actionButtonSize - Math.max(actionButtonSize - elementRightTop90.y, 0),
        y:
          actionButtonSize -
          (actionButtonSize - Math.min(draggingAreaRightBottom.x - elementRightTop90.x, actionButtonSize)),
      },
      resizeOffset: {
        x: actionButtonSize - Math.min(draggingAreaRightBottom.y - elementLeftBottom90.y, actionButtonSize),
        y: actionButtonSize - (actionButtonSize - Math.max(actionButtonSize - elementLeftBottom90.x, 0)),
      },
      rotateOffset: {
        x: actionButtonSize - Math.min(draggingAreaRightBottom.y - elementRightBottom90.y, actionButtonSize),
        y:
          actionButtonSize -
          (actionButtonSize - Math.min(draggingAreaRightBottom.x - elementRightBottom90.x, actionButtonSize)),
      },
    };
  } else if (direction === 180) {
    return {
      deleteOffset: {
        x:
          actionButtonSize -
          (actionButtonSize - Math.min(draggingAreaRightBottom.x - elementRightBottom180.x, actionButtonSize)),
        y:
          actionButtonSize -
          (actionButtonSize - Math.min(draggingAreaRightBottom.y - elementRightBottom180.y, actionButtonSize)),
      },
      resizeOffset: {
        x: actionButtonSize - (actionButtonSize - Math.max(actionButtonSize - elementLeftTop180.x, 0)),
        y: actionButtonSize - (actionButtonSize - Math.max(actionButtonSize - elementLeftTop180.y, 0)),
      },
      rotateOffset: {
        x: actionButtonSize - (actionButtonSize - Math.max(actionButtonSize - elementLeftBottom180.x, 0)),
        y:
          actionButtonSize -
          (actionButtonSize - Math.min(draggingAreaRightBottom.y - elementLeftBottom180.y, actionButtonSize)),
      },
    };
  } else if (direction === 270) {
    return {
      deleteOffset: {
        x:
          actionButtonSize -
          (actionButtonSize - Math.min(draggingAreaRightBottom.y - elementLeftBottom270.y, actionButtonSize)),
        y: actionButtonSize - Math.max(actionButtonSize - elementLeftBottom270.x, 0),
      },
      resizeOffset: {
        x: actionButtonSize - (actionButtonSize - Math.max(actionButtonSize - elementRightTop270.y, 0)),
        y: actionButtonSize - Math.min(draggingAreaRightBottom.x - elementRightTop270.x, actionButtonSize),
      },
      rotateOffset: {
        x: actionButtonSize - (actionButtonSize - Math.max(actionButtonSize - elementLeftTop270.y, 0)),
        y: actionButtonSize - Math.max(actionButtonSize - elementLeftTop270.x, 0),
      },
    };
  } else {
    return {
      deleteOffset: {
        x: actionButtonSize - Math.max(actionButtonSize - elementLeftTop.x, 0),
        y: actionButtonSize - Math.max(actionButtonSize - elementLeftTop.y, 0),
      },
      resizeOffset: {
        x: actionButtonSize - Math.min(draggingAreaRightBottom.x - elementRightBottom.x, actionButtonSize),
        y: actionButtonSize - Math.min(draggingAreaRightBottom.y - elementRightBottom.y, actionButtonSize),
      },
      rotateOffset: {
        x: actionButtonSize - Math.min(draggingAreaRightTop.x - elementRightTop.x, actionButtonSize),
        y: actionButtonSize - Math.max(actionButtonSize - elementRightTop.y, 0),
      },
    };
  }
};
